import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { SectionHeading } from '~/components/SectionHeading'
import { attempt } from '~/api/attempt'
import { api } from '~/api/client'
import type { Appearance, ArtistKnown, ArtistPageResponse, ArtistSummary } from '~/api/types'
import { useArtwork } from '~/api/artwork'
import { bubbleFor } from '~/components/bubbles'
import { AppLink } from '~/components/AppLink'
import { FixArtist } from '~/components/FixArtist'
import { FoldInto } from '~/sheet/ArtistSheet'
import { useViewer } from '~/Viewer'

import s from './Artist.module.css'
import { Star } from '~/components/Star'

/**
 * One act: where they are playing, where they have played, and the rooms they play in.
 *
 * A page rather than the dialog it used to be. A gig is something you glance at and go back
 * from, which is why `/e/<id>` opens over whatever you were reading; an act is somewhere you
 * arrive, and the answer to "where else are they playing" does not fit in a sheet.
 *
 * **Built for one date, not for twenty.** 8,600 of the 10,736 acts in the database have
 * exactly one, and 269 have any past at all — so the figures are suppressed below two dates
 * and the room tally collapses to a sentence below two rooms. Six figures reading
 * 1, 0, 1, 1 is not a summary, and a bar at 100% beside a single name is a chart of nothing.
 */
const NIGHT = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/London',
})

const TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/London',
})

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'Europe/London' })

/** "1 night", not "1 nights". */
function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

function money(pence: number | null): string {
  if (!pence) return ''
  const pounds = pence / 100
  return `£${pounds % 1 === 0 ? pounds.toFixed(0) : pounds.toFixed(2)}`
}

function tally(dates: Appearance[]): [string, string, number][] {
  const rooms = new Map<string, [string, number]>()
  for (const date of dates) {
    const [, n] = rooms.get(date.venue_slug) ?? [date.venue, 0]
    rooms.set(date.venue_slug, [date.venue, n + 1])
  }
  return [...rooms.entries()]
    .map(([slug, [name, n]]) => [slug, name, n] as [string, string, number])
    .sort((a, b) => b[2] - a[2] || a[1].localeCompare(b[1]))
}

function Head({ children, meta }: { children: string; meta?: string | null }) {
  return (
    <div className={s.sechead}>
      <SectionHeading>{children}</SectionHeading>
      {meta ? <span className={s.meta}>{meta}</span> : null}
    </div>
  )
}

function Figure({ n, unit, one, many }: { n: number | string; unit?: string; one: string; many: string }) {
  return (
    <div className={s.figure}>
      <span className={s.value}>
        <b>{n}</b>
        {unit ? <i>{unit}</i> : null}
      </span>
      <span className={s.mono}>{n === 1 ? one : many}</span>
    </div>
  )
}

/**
 * A photograph of them, a poster from one of their nights, or a bubble.
 *
 * Its own component so the step-down can live beside it: `useArtwork` is a hook, this page
 * returns early three times before the poster is known, and a hook after a return is not a
 * hook. Every card on the site already steps down and this page did not — it drew the sized
 * URL raw, so a Ticketmaster asset with no generated variant printed a broken picture where
 * the cards would have shown the original.
 */
function Picture({ artist, poster }: { artist: ArtistSummary; poster: string | null }) {
  const hero = useArtwork(poster)
  const portrait = artist.known?.portrait ?? null
  if (portrait) {
    return (
      /* A Commons file is licensed individually, so the credit is per picture and travels
         with it. The file page carries the photographer and the terms, which is what a link
         to it credits. */
      <a
        href={artist.known?.portrait_page ?? undefined}
        target="_blank"
        rel="noreferrer noopener"
      >
        <img src={portrait} alt={artist.name} decoding="async" />
      </a>
    )
  }
  if (hero.src && !hero.broken) return <img src={hero.src} alt="" decoding="async" onError={hero.onError} />
  /* Neither a photograph of them nor a poster for anything they are playing, so a bubble —
     the same one the bill and the search results give them. It said "no picture", which is
     true and is the largest element on the page saying it. */
  return <img className={s.bubble} src={bubbleFor(`a-${artist.id}`)} alt="" decoding="async" />
}

/**
 * Naming the act a night actually belonged to.
 *
 * Pre-filled with what the listing actually printed for this booking, falling back to the
 * act's own name on a booking older than that column. This is the whole point of keeping it:
 * the shared row is the singer `ADÉLA`, the night being moved off is a DJ the listing spelt
 * `Adela`, and offering her name back was offering the one answer that is certainly wrong.
 *
 * Pressing without editing is not a trap. The server numbers the *slug* rather than the
 * name, so the new row keeps whatever is typed and the two are simply two rows. It went
 * wrong twice on the way here: first refusing the shared name outright, then renaming the
 * act to `ADÉLA (2)` — which says a random DJ is a second version of a Slovak
 * singer-songwriter.
 */
function NotThem({
  artistId,
  name: shared,
  date,
  onDone,
  onCancel,
}: {
  artistId: number
  name: string
  date: Appearance
  onDone(landed: number): void
  onCancel(): void
}) {
  // What the listing printed, falling back to the act's own name on a booking older than the
  // column. `spelling` is read first for the reader's sake rather than for a difference it
  // makes: `calendar._spelled_differently` answers either `None` or `billed_as` verbatim, so
  // the two can never disagree and the first term never changes the answer — demonstrated in
  // `tests/test_spellings.py`, over a table of listings, because this used to be described as
  // a precedence that mattered. Kept so the judged field is the one this reads, and it is the
  // field the API's shape would have to change to make different.
  const [name, setName] = useState(date.spelling?.trim() || date.billed_as?.trim() || shared)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  return (
    <form
      className={s.notthemform}
      onSubmit={event => {
        event.preventDefault()
        setBusy(true)
        setFailed(null)
        void api
          .separate(artistId, date.id, name.trim())
          .then(answer => onDone(answer.artist))
          .catch((error: unknown) => setFailed(error instanceof Error ? error.message : String(error)))
          .finally(() => setBusy(false))
      }}
    >
      <p className={s.blurb}>
        <b>{date.title}</b> on {date.day} at {date.venue} — who was actually playing?
      </p>
      <label>
        <span className={s.mono}>The other act</span>
        <input
          value={name}
          autoFocus
          aria-label="The other act"
          maxLength={120}
          onChange={event => setName(event.target.value)}
        />
      </label>
      <span className={s.notthemrow}>
        <button type="submit" className={s.pick} disabled={busy || !name.trim()}>
          {busy ? 'Moving…' : 'Move this night'}
        </button>
        <button type="button" className={s.pick} onClick={onCancel}>
          Cancel
        </button>
      </span>
      {/* What the server said. It refuses a name that does not tell the two apart, and that
          refusal is the most likely one a person will meet here. */}
      {failed ? <span className={s.trouble}>{failed}</span> : null}
    </form>
  )
}

function Night({ date, onSeparate }: { date: Appearance; onSeparate?: () => void }) {
  const when = new Date(date.starts_at)
  // The same step-down every card on the site uses. Ticketmaster names variants in the path
  // and has none generated for about a tenth of its assets, so the sized URL the server
  // built answers 403 while the original answers 200 — and this page was the one place that
  // drew the sized one raw and let the browser print a broken picture.
  const art = useArtwork(date.image_url)
  return (
    <li className={s.row}>
      <span className={s.thumb}>
        {/* A bubble when there is no picture, or when the one there is refuses to load —
            the same fallback the hero above this list uses, and the same one the card for
            this gig uses everywhere else. It drew nothing at all, so a date whose artwork
            403s left an empty grey box in a column of pictures.

            Keyed on the gig, so a night wears the same bubble here as on its own card. */}
        <img
          src={art.src && !art.broken ? art.src : bubbleFor(date.id)}
          alt=""
          loading="lazy"
          decoding="async"
          onError={art.onError}
        />
      </span>
      <span className={s.when}>
        <b>{NIGHT.format(when)}</b>
        <i>{TIME.format(when)}</i>
      </span>
      <AppLink className={s.what ?? ''} to={`/e/${date.id}`}>
        <b>{date.title}</b>
        {/* What this listing called the act, where that is not how the act's own name is
            spelled. Everybody sees it, not only the editor: it is what the listing printed
            rather than a moderation note, and on a row two acts share it is the only thing on
            the page that lets a reader tell the two apart.

            The server decides whether there is anything to print — see
            `calendar._spelled_differently`. Case and Unicode normalisation are not a different
            spelling, so a shouted listing prints nothing here. */}
        {date.spelling ? <small className={s.spelt}>billed as {date.spelling}</small> : null}
        {date.festival ? <small>{date.festival}</small> : null}
      </AppLink>
      <AppLink className={s.where ?? ''} to={`/v/${date.venue_slug}`}>
        {date.venue}
      </AppLink>
      <span className={s.mark}>
        {date.lifecycle_status !== 'scheduled' ? (
          <b className={s.dead}>{date.lifecycle_status}</b>
        ) : (
          <>
            {/* Only where a source stated it — see `Performance.role_stated`. An act's own
                page listed "support" against every date it had, on the strength of the feed
                having printed somebody else's name first. */}
            <span className={s.role}>
              {date.role_stated ? (date.role === 'headliner' ? 'headline' : date.role) : ''}
            </span>
            {date.price_min ? <i className={s.price}>{money(date.price_min)}</i> : null}
          </>
        )}
        {/* Per date, because that is the grain the mistake has: two acts share a row and
            only some of the nights on it are the other one. Editor only, and absent for
            everybody else, so the column stays quiet on a page a reader is looking at. */}
        {onSeparate ? (
          <button type="button" className={s.notthem} onClick={onSeparate}>
            not them
          </button>
        ) : null}
      </span>
    </li>
  )
}

/**
 * What MusicBrainz says, drawn as theirs rather than as the calendar's own.
 *
 * Unattributed on the page, which is Bernardo's call and a legal one: MusicBrainz core
 * data is CC0, so the genres, the area, the year and the links carry no attribution
 * requirement. Anything added later from Wikipedia would — that text is CC BY-SA — and the
 * attribution for it is a link to the article rather than a label on the block.
 *
 * Nothing at all when there is nothing, which is most of the time. Half the acts here are
 * not in MusicBrainz and two matches in five carry no genre, no area and no link.
 */
/**
 * A listener count as a person would say it. 3,825,461 is a number to parse; 3.8M is a size.
 *
 * The point of the figure on an act's page is the order of magnitude — is this somebody
 * thousands of people listen to, or millions — and every digit past the first two is noise
 * against that question.
 */
function listeners(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 10_000) return `${Math.round(n / 1000)}k`
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}

function Known({ known }: { known: ArtistKnown | null }) {
  if (!known) return null
  const year = known.began
    ? `since ${known.began.slice(0, 4)}`
    : known.born
      ? `born ${known.born.slice(0, 4)}`
      : null
  const origin = [known.kind, known.area, year].filter(Boolean).join(' · ')
  const links = Object.entries(known.links)
  return (
    <div className={s.known}>
      {origin || known.listeners ? (
        <p className={s.origin}>
          {[origin, known.listeners ? `${listeners(known.listeners)} listeners` : '']
            .filter(Boolean)
            .join(' · ')}
        </p>
      ) : null}
      {/* The article's opening, which is a sentence or two about who somebody is rather
          than an encyclopedia entry on a gig listing. Never without the link beside it:
          the text is CC BY-SA and that link is what credits it. */}
      {known.description && (known.wikipedia || known.lastfm) ? (
        <p className={s.blurb}>
          {known.description}{' '}
          {/* Whose words these are. Both sources are CC BY-SA, so the link is not a courtesy
              — the text may not be shown without it, which is why the description is drawn
              only when one of the two is there to name. */}
          <a
            href={known.wikipedia ?? known.lastfm ?? undefined}
            target="_blank"
            rel="noreferrer noopener"
          >
            {known.wikipedia ? 'Wikipedia' : 'Last.fm'}
          </a>
        </p>
      ) : null}
      {known.genres.length > 0 ? (
        <ul className={s.genres}>
          {known.genres.map(genre => (
            <li key={genre}>
              {/* The reason to know an act is drum and bass is to find the other
                  thirty-two, so the chip is the way there. */}
              <AppLink to={`/g/${encodeURIComponent(genre)}`}>{genre}</AppLink>
            </li>
          ))}
        </ul>
      ) : null}
      {links.length > 0 ? (
        <p className={s.links}>
          {links.map(([kind, href]) => (
            <a key={kind} href={href} target="_blank" rel="noreferrer noopener">
              {kind === 'official homepage' ? 'website' : kind}
            </a>
          ))}
        </p>
      ) : null}
    </div>
  )
}

export function Artist() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const me = useViewer() ?? null
  const [page, setPage] = useState<ArtistPageResponse | null>(null)
  const [missing, setMissing] = useState(false)
  const [following, setFollowing] = useState<boolean | null>(null)
  // Which act the page is showing right now, for a revert that lands after a navigation.
  const showing = useRef(Number(id))
  showing.current = Number(id)
  // Bumped to ask again for the same act. A decision that renames a row without folding it
  // leaves the address exactly where it was, so there is nothing for the router to change
  // and nothing to make the page notice.
  const [again, setAgain] = useState(0)
  // Which date, if any, is being moved off this act. One at a time: the form asks for a
  // name and the name is about that night, so two open at once would be two questions
  // wearing one answer.
  const [notThem, setNotThem] = useState<Appearance | null>(null)

  const artistId = Number(id)

  useEffect(() => {
    if (!Number.isFinite(artistId)) {
      setMissing(true)
      return
    }
    let live = true
    setPage(null)
    setFollowing(null)
    api
      .artist(artistId)
      .then(found => live && setPage(found))
      .catch(() => live && setMissing(true))
    return () => {
      live = false
    }
  }, [artistId, again])

  if (missing) {
    return (
      <main className={s.wrap}>
        <h1 className={s.name}>No such act.</h1>
      </main>
    )
  }
  if (!page) return <main className={s.wrap} aria-busy="true" />

  const { artist, appearances, tonight } = page
  const coming = appearances.filter(a => a.night >= tonight)
  const played = appearances.filter(a => a.night < tonight).reverse()
  const rooms = tally(appearances)
  // Counted over `coming`, not over every appearance: this number stands beside the nights
  // ahead in one sentence, and the room tally below it answers a different question.
  const comingRooms = tally(coming)
  const first = played[played.length - 1]
  const followed = following ?? artist.is_favourite
  // The poster of their next date, because nothing in the database depicts an act. Named
  // as what it is rather than dressed as a portrait.
  //
  // Only from a night they headline that is not a festival. Taken from any appearance at
  // all, it dressed acts in other people's artwork: a slot on an All Points East bill gave
  // them the festival's banner as their picture, and a support booking gave them the
  // headliner's poster. Neither is a picture of them even in the loose sense this is.
  //
  // No fallback to the ones it rules out. The dates below still show every poster on its own
  // row, where it is the night's artwork and reads as one — the thing worth avoiding is the
  // same image at the top of the page, captioned with somebody else's name.
  const theirs = (a: Appearance) => a.role === 'headliner' && !a.festival && a.image_url
  // A real picture of the act where there is one, and the poster only where there is not —
  // `Picture` decides between them. Wikidata reaches about one act in six here and misses
  // every club booking, so the fallback is not a stopgap: it is what most pages go on
  // showing.
  const poster = coming.find(theirs)?.image_url ?? played.find(theirs)?.image_url ?? null

  /* One follow, two ways to press it. Shared so they cannot disagree on screen. */
  const follow = () => {
    const on = !followed
    setFollowing(on)
    // Patched first, written second, and put back if the write does not take. This page
    // is the one FIXED.md names as having hidden a 400 behind its own optimistic state.
    // Keyed to the act that was pressed: a refusal arriving after the reader has moved to
    // another act's page must not flip that act's star.
    void attempt(() => api.star(artistId, on)).then(took => {
      if (!took && showing.current === artistId) setFollowing(!on)
    })
  }

  return (
    <main className={s.wrap}>
      <header className={s.head}>
        <span className={s.plate}>
          {/* No star over the picture any more. It called the same `follow` as the button
              below with the same state, so an editor was given one control twice — and a
              glyph that cannot say what following *does* is the worse of the two to keep. */}
          <Picture artist={artist} poster={poster} />
        </span>
        <div className={s.who}>
          <h1 className={s.name}>{artist.name}</h1>
          {artist.aliases.length > 0 ? (
            <p className={s.aka}>also billed as {artist.aliases.join(' · ')}</p>
          ) : null}
          <Known known={artist.known} />
          {/* Nothing here when there is nothing coming: the section below says so once,
              and saying it twice on one screen reads as two different absences. */}
          {coming.length > 0 ? (
            <p className={s.standing}>
              <b>{count(coming.length, 'night', 'nights')}</b> coming up
              {comingRooms.length > 1 ? (
                <>
                  {' '}
                  across <b>{count(comingRooms.length, 'room', 'rooms')}</b>
                </>
              ) : null}
              .
            </p>
          ) : null}

          {/*
            * Following an act, said plainly, and the only place it is offered.
            *
            * It was editor-only and a bare glyph sat over the picture for everybody else, so
            * the two readers of this page were given two different controls for one gesture
            * — and the editor was given both. One labelled button now, shaped like Save and
            * Going on a gig: the same act in the same place, whoever is reading.
            *
            * The words still differ by who is pressing, because the press does. An editor's
            * publishes every headline date to the calendar everybody reads; a reader's ranks
            * those dates higher for themselves and nobody else sees it.
            */}
          {me ? (
            <button
              type="button"
              className={followed ? `${s.star} ${s.on}` : s.star}
              aria-pressed={followed}
              aria-label={
                me.isEditor
                  ? followed
                    ? 'On the calendar. Take their dates off'
                    : 'Put their dates on the calendar'
                  : followed
                    ? 'Following them. Stop'
                    : 'Follow them, so their dates rank higher for you'
              }
              onClick={follow}
            >
              <Star fill={followed} size={13} strokeWidth={1.8} />
              {me.isEditor
                ? followed
                  ? 'On the calendar'
                  : 'Put their dates on the calendar'
                : followed
                  ? 'Following'
                  : 'Follow'}
            </button>
          ) : null}
        </div>
      </header>

      {/* Below two dates a row of figures is not a summary. */}
      {appearances.length > 1 ? (
        <section className={s.figures}>
          <Figure n={coming.length} one="night coming up" many="nights coming up" />
          <Figure n={played.length} one="night played" many="nights played" />
          <Figure n={rooms.length} one="room" many="rooms" />
          {first ? (
            <Figure
              n={first.night.slice(8).replace(/^0/, '')}
              unit={
                Number(first.night.slice(0, 4)) === new Date().getUTCFullYear()
                  ? MONTH.format(new Date(first.starts_at))
                  : `${MONTH.format(new Date(first.starts_at))} ${first.night.slice(0, 4)}`
              }
              one="first night here"
              many="first night here"
            />
          ) : null}
        </section>
      ) : null}

      <section>
        {/* Named apart on purpose. `Playing` and `Played` differ by one letter, so two
            headings on one screen read as the same word twice and a reader has to work out
            which half of the page they are in from the dates under it. */}
        <Head meta={coming.length ? count(coming.length, 'night', 'nights') : null}>
          Scheduled
        </Head>
        {coming.length === 0 ? (
          <p className={s.none}>No dates on the calendar.</p>
        ) : (
          <ul className={s.rows}>
            {coming.map(date => (
              <Night
                key={date.id}
                date={date}
                {...(me?.isEditor ? { onSeparate: () => setNotThem(date) } : {})}
              />
            ))}
          </ul>
        )}
      </section>

      {played.length > 0 ? (
        <section>
          <Head meta={count(played.length, 'night', 'nights')}>History</Head>
          <ul className={s.rows}>
            {played.map(date => (
              <Night
                key={date.id}
                date={date}
                {...(me?.isEditor ? { onSeparate: () => setNotThem(date) } : {})}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {rooms.length > 1 ? (
        <section>
          <Head meta={count(rooms.length, 'room', 'rooms')}>Rooms</Head>
          <ul className={s.tally}>
            {rooms.slice(0, 10).map(([slug, name, n]) => (
              <li key={slug}>
                <AppLink to={`/v/${slug}`}>{name}</AppLink>
                <i style={{ width: `${(n / (rooms[0]?.[2] ?? 1)) * 100}%` }} />
                <b className={s.mono}>{n}</b>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {me?.isEditor && notThem ? (
        <section>
          {/* Above "Same act", because it is the same kind of decision pointing the other
              way, and because the press that opens it is up in the list. */}
          <Head>A different act</Head>
          <NotThem
            artistId={artistId}
            name={artist.name}
            date={notThem}
            onCancel={() => setNotThem(null)}
            onDone={() => {
              setNotThem(null)
              setAgain(n => n + 1)
            }}
          />
        </section>
      ) : null}

      {me?.isEditor ? (
        <section>
          <Head>Same act</Head>
          <FoldInto
            artistId={artistId}
            name={artist.name}
            onDone={() => navigate('/calendar')}
          />
        </section>
      ) : null}

      {me?.isEditor ? (
        <section>
          {/* The queue's verdicts, offered where you find the row rather than where a rule
              sent you. No rule caught `Secret Guest b2b Special Guest`, so the only way to
              reach it was to already know it was wrong. */}
          <Head>What this row is</Head>
          <FixArtist
            artistId={artistId}
            name={artist.name}
            onDone={landed => {
              // A rename onto a name that already exists folds this row into that one, and
              // this id stops existing — so it goes to whichever row the bills ended up on
              // rather than reloading a page that is no longer there.
              if (landed !== artistId) navigate(`/a/${landed}`)
              else setAgain(n => n + 1)
            }}
          />
        </section>
      ) : null}
    </main>
  )
}
